<template>
  <div class="report">
    <innertopbar @close="closeWindow()">
      <template v-slot:breadcrumb>
        <router-link to="school-report-list"> School Reporting </router-link>
      </template>

      <template v-slot:listitems>
        <li
          class="inner-head-list-item hover:opacity-50 hover:text-action-blue"
        >
          <button
            v-bind:class="checkTab(1)"
            v-on:click="showAssessment()"
            class="focus:outline-none"
          >
            Assessment
          </button>
        </li>
        <li v-if="!hasMultipleSelection"
          class="inner-head-list-item hover:opacity-50 hover:text-action-blue"
        >
          <button
            v-bind:class="checkTab(2)"
            v-on:click="showActivities()"
            class="focus:outline-none"
          >
            Extra Curricular
          </button>
        </li>
        <li v-if="!hasMultipleSelection"
          class="inner-head-list-item hover:opacity-50 hover:text-action-blue"
        >
          <button
            v-bind:class="checkTab(3)"
            v-on:click="showEvents()"
            class="focus:outline-none"
          >
            Events
          </button>
        </li>
      </template>
    </innertopbar>

    <div class="filter-bar">
      <ul class="actions">
        <li class="opacity-75">Filter by:</li>
        <li>
          <button
            type="button"
            class="filter-button focus:outline-none hover:opacity-75"
            title="Filter"
            @click="setFilter()"
          >
            <i
              class="fas fa-user-friends"
              v-bind:class="
                showFilter ? 'bg-action-blue text-white' : 'bg-gray-400'
              "
            ></i>
            Pupils
          </button>
        </li>

        <li class="opacity-75">Selected schools:</li>

        <li
          v-for="item in schools"
          :key="item.id"
          class="inline-block mr-2"
        >

        
          <button
            type="button"
            @click="selectSchool(item)"
            class="
              cursor-pointer
              font-bold
              py-2
              px-4
              rounded
              hover:bg-gray-400 hover:bg-opacity-50 hover:text-action-blue
            "
            v-bind:class="
              schoolIsSelected(item)
                ? 'bg-action-blue bg-opacity-75 text-white'
                : 'bg-gray-100 text-gray-500'
            "
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </div>

    <filterSection
      class=""
      :fullView="false"
      :classes="classesArray"
      :filterOpen="showFilter"
      v-if="showFilter"
      @classUpdated="updateFilteredClass"
      @yearsUpdated="updateFilteredYears"
      @typesUpdated="updateFilteredTypes"
      @calendarUpdated="updateSelectedCalendarYear"
    >
    </filterSection>

    <transition name="fade" v-if="loading">
      <div class="loading">
        <span class="fa fa-spinner fa-spin"></span> Loading Reports
      </div>
    </transition>

    <div class="w-full mx-auto">
      <div class="flex flex-col md:flex-row">
        <div
          i
          class="
            w-95p
            rounded-lg
            h-screen
            overflow-y-scroll
            bg-white
            mt-2
            pb-4
            mx-auto
          "
        >

          <div v-if="errored" class="empty">
            <p>
              We are not able to retrieve this information at the moment, please
              try back later
            </p>
          </div>

          <div id="content-area" v-else>
            <div v-if="componentLoaded">
        
              <div v-show="isSelectedContent(1)">
                
                <template v-if="hasMultipleSelection">
                  
                  <orgAssessmentChart
                    :data="orderedAssessments"
                  ></orgAssessmentChart>
                </template>

                <template v-else>
                  <assessmentChart
                    :assessmentData="orderedAssessments"
                  ></assessmentChart>
                </template>
              </div>

              <div v-if="isSelectedContent(2)">
                                <activityChart
                  :activities="activitiesFiltered"
                  :pupilCount="pupilArrayCount"
                ></activityChart>
                <activityExtract
                  :activities="activitiesFiltered"
                  :years="years"
                  :types="types"
                ></activityExtract>
              </div>

              <div v-if="isSelectedContent(3)">
                <eventChart
                  :events="eventsFiltered"
                  :pupilCount="pupilArrayCount"
                ></eventChart>
                <eventExtract
                  :events="eventsFiltered"
                  :years="years"
                  :types="types"
                ></eventExtract>
              </div>
            </div>
            <p v-else class="p-4">
              We could not find any data for your given selection.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const innertopbar = () => import("@/components/InnerTopBar.vue");

const assessmentChart = () =>
  import("@/components/charts/TheAssessmentChart.vue");
const orgAssessmentChart = () =>
  import("@/components/charts/TheOrgAssessmentChart.vue");
const activityChart = () => import("@/components/charts/TheActivityChart.vue");
const activityExtract = () =>
  import("@/components/reports/TheActivityData.vue");
const eventChart = () => import("@/components/charts/TheEventChart.vue");
const eventExtract = () => import("@/components/reports/TheEventData.vue");
const filterSection = () => import("@/components/helpers/PupilFilter.vue");

import { calendarYearList, filterTypeList } from "../assets/js/utils";

import { SchoolService } from "@/services/school.service";
import { assessmentMixin } from "@/mixins/assessmentMixin.js";
import { pupilMixin } from "@/mixins/pupilMixin.js";

export default {
  name: "report",
  mixins: [assessmentMixin, pupilMixin],
  props: {
    schools: { type: Array, required: true },
  },
  computed: {
    hasMultipleSelection() {
      return this.selectedSchools.length > 1;
    },
    componentLoaded() {
      return this.loading === false;
    },
    orderedAssessments() {
      var vm = this;
      const schoolsInView = vm.selectedSchools.map((s) => s.id);
      let pupilAssessments = vm.pupilAssessments;
      let years = vm.filteredYears;
      let types = vm.filteredTypes;

      if (this.hasMultipleSelection) {
        const schoolGrades = pupilAssessments.filter((s) =>
          schoolsInView.includes(s.id)
        );

        const results = schoolGrades.map(function (res) {
          return {
            id: res.id,
            name: res.name,
            scores: vm.parseToScores(res.values, years, types, null),
          };
        });

        return results;
      } else {
        const schoolGrades = pupilAssessments.find((s) => s.id === schoolsInView[0]);
        return this.parseToScores(pupilAssessments, years, types, null);
      }
    },
    activitiesFiltered() {
      var vm = this;
      if (vm.showFilter === true) {
        var pupils = this.filterPupils(
          vm.filteredYears,
          vm.filteredTypes,
          vm.filteredClass
        );
        const uniquePupils = [
          ...new Set(pupils.map((item) => item.identifier)),
        ];

        return this.filterActivitiesOnPupils(uniquePupils);
      } else {
        return vm.activities;
      }
    },
    eventsFiltered() {
      var vm = this;
      if (vm.showFilter === true) {
        var pupils = this.filterPupils(
          vm.filteredYears,
          vm.filteredTypes,
          vm.filteredClass
        );
        const uniquePupils = [
          ...new Set(pupils.map((item) => item.identifier)),
        ];

        return this.filterEventsOnPupils(uniquePupils);
      } else {
        return vm.events;
      }
    },
  },
  data() {
    return {
      currentTab: 1,
      errored: false,
      loading: true,
      showFilter: false,
      assessments: [],

      selectedCalendar: {},

      filteredYears: [],
      filteredTypes: [],
      filteredClass: {},
      selectedSchools: [...this.schools],
      types: filterTypeList(),
      calendarList: [],
    };
  },
  created() {
    if (this.schools == undefined) {
      this.closeWindow();
      return;
    } else {
      this.fetchCalendars();
    }
  },
  mounted() {

  },
  methods: {
    refresh() {
      var vm = this
      vm.schools.forEach(function (s) {
        vm.fetchAssessment(s);
        vm.fetchReportingData(
          vm.selectedCalendar.value,
          "reporting/consuls/" + s.id
        );
      });
    },
    fetchCalendars() {
      this.calendarList = calendarYearList();
      this.updateCalendarYear(this.calendarList[0]);
    },
    updateCalendarYear(cal) {
      this.selectedCalendar = cal;
      this.refresh();
    },
    fetchAssessment(school) {
      SchoolService.getAll(
        "reporting/consuls/" +
          school.id +
          "/assessments?stored=" +
          this.selectedCalendar.value
      )
        .then((response) => this.buildAssessmentDict(school, response.data.data))
        .catch((err) => {
          console.log("Activities " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    buildAssessmentDict(school, data) {
      this.assessments.push({
        id: school.id,
        name: school.name,
        values: data,
      });
    },
    setFilter() {
      this.showFilter = !this.showFilter;
      if (this.showFilter === false) {
        this.clearFilter();
      }
    },
    clearFilter() {
      this.filteredClass = {};
      this.filteredYears = [];
      this.filteredTypes = [];
    },
    updateFilteredClass(value) {
      this.filteredClass = value;
    },
    updateFilteredYears(values) {
      this.filteredYears = values;
    },
    updateFilteredTypes(values) {
      this.filteredTypes = values;
    },
    updateSelectedCalendarYear(cal) {
      this.selectedCalendar = cal;
      this.refresh();
    },
    schoolIsSelected(school) {
      let found = this.selectedSchools.find(
        (s) => s.id === school.id
      );
      return found == undefined ? false : true;
    },
    selectSchool(school) {
      if (this.schoolIsSelected(school)) {
        if (this.selectedSchools.length > 1)
          this.selectedSchools.splice(
            this.selectedSchools.findIndex(
              (item) => item.id === school.id
            ),
            1
          );
      } else {
        this.selectedSchools.push(school);
      }
    },
    isSelectedContent(value) {
      return this.currentTab == value ? true : false;
    },
    checkTab(value) {
      return this.currentTab == value ? "active" : "";
    },
    showAssessment() {
      this.currentTab = 1;
    },
    showEvents() {
      this.currentTab = 3;
    },
    showActivities() {
      this.currentTab = 2;
    },
    closeWindow() {
      this.$router.push({ path: "school-report-list" });
      window.scrollTo(0, 0);
    },
  },
  components: {
    assessmentChart,
    orgAssessmentChart,
    filterSection,
    innertopbar,
    activityChart,
    activityExtract,
    eventChart,
    eventExtract,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
